<script>
import { mapActions } from 'vuex';

export default {
  name: 'IniciarConversaModal',
  props: {
    // Contato pré-selecionado (ex.: vindo de um participante de grupo).
    // Basta { id, nome } — o modal já pula para a etapa 2 na montagem.
    contatoInicial: { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      etapa: 1,                  // 1=selecionar contato, 2=enviar mensagem
      modoSelecao: 'existente',  // 'existente' | 'novo'
      // etapa 1 — buscar contato existente
      busca: '',
      buscando: false,
      resultados: [],
      contatoSelecionado: null,
      // etapa 1 — novo número
      novoTelefone: '',
      novoNome: '',
      // etapa 2 — janela + envio
      janela: null,            // { aberta, ultima_entrada, expira_em, motivo, consentimento }
      verificando: false,
      modoEnvio: 'texto',      // 'texto' | 'template'
      conteudo: '',
      // Canais disponíveis (carregados na montagem). Atendente escolhe por
      // qual número enviar — templates são filtrados pelo canal selecionado.
      canais: [],
      canalSelecionadoId: null,
      carregandoCanais: false,
      templates: [],
      templateName: '',
      templateValores: {},
      sincronizandoTemplates: false,
      enviando: false,
      erro: null,
      sucesso: null,
      debounceTimer: null,
    };
  },
  async mounted() {
    await this._carregarCanais();
    // Abertura com contato pré-selecionado (ex.: a partir de um grupo).
    if (this.contatoInicial && this.contatoInicial.id) {
      this.contatoSelecionado = this.contatoInicial;
      this.modoSelecao = 'existente';
      await this.avancar();
    }
  },
  watch: {
    templateName() { this._prefillTemplate(); },
    async canalSelecionadoId(novo, antigo) {
      // Trocou de canal → recarrega templates desse canal e limpa seleção
      // pra não enviar template de outro canal.
      if (novo === antigo) return;
      this.templates = [];
      this.templateName = '';
      this.templateValores = {};
      if (this.etapa === 2) await this._carregarTemplatesDoCanal();
    },
  },
  computed: {
    podeAvancar() {
      if (this.modoSelecao === 'existente') return Boolean(this.contatoSelecionado);
      const tel = (this.novoTelefone || '').replace(/\D/g, '');
      return tel.length >= 10;
    },
    contatoAtivoNome() {
      if (this.contatoSelecionado) return this.contatoSelecionado.nome || `Contato #${this.contatoSelecionado.id}`;
      return this.novoNome || `+55 ${this.novoTelefone}`;
    },
    templateAtual() {
      return this.templates.find((t) => t.name === this.templateName) || null;
    },
    podeEnviar() {
      if (this.enviando) return false;
      if (!this.canalSelecionadoId) return false;
      if (this.modoEnvio === 'texto') return Boolean((this.conteudo || '').trim());
      if (!this.templateAtual) return false;
      const obrigatorios = (this.templateAtual.parametros || []).filter((p) => p.obrigatorio);
      return obrigatorios.every((p) => Boolean((this.templateValores[p.nome] || '').trim()));
    },
    // Regras combinadas: o que está bloqueado e por quê
    bloqueio() {
      if (!this.janela) return null;
      if (this.janela.consentimento === 'opt_out') {
        return { tipo: 'opt_out', mensagem: 'Filiado tem opt-out registrado. Não é possível enviar (LGPD).' };
      }
      const semOptIn = this.janela.consentimento !== 'opt_in';
      const janelaFechada = !this.janela.aberta;
      if (semOptIn || janelaFechada) {
        return {
          tipo: 'precisa-template',
          mensagem: semOptIn
            ? 'Sem opt-in registrado. Use um template HSM para iniciar (ao enviar, registramos opt-in implícito).'
            : `Janela 24h fechada (última mensagem do filiado: ${this._fmt(this.janela.ultima_entrada) || 'nunca'}). Use um template HSM.`,
        };
      }
      return null;
    },
  },
  methods: {
    ...mapActions('atendimento', [
      'buscarContato',
      'verificarJanela',
      'fetchTemplates',
      'fetchCanaisWhatsapp',
      'sincronizarTemplates',
      'iniciarConversa',
    ]),

    async _carregarCanais() {
      this.carregandoCanais = true;
      try {
        const lista = await this.fetchCanaisWhatsapp();
        this.canais = lista || [];
        if (!this.canalSelecionadoId && this.canais.length) {
          // Prioriza o canal conectado mais recentemente
          const ordenado = [...this.canais].sort((a, b) => (
            new Date(b.conectado_em || 0) - new Date(a.conectado_em || 0)
          ));
          this.canalSelecionadoId = ordenado[0].id;
        }
      } catch (error) {
        this.erro = `Falha ao carregar canais WhatsApp: ${error.message}`;
      } finally {
        this.carregandoCanais = false;
      }
    },

    async _carregarTemplatesDoCanal() {
      if (!this.canalSelecionadoId) {
        this.templates = [];
        return;
      }
      try {
        const resp = await this.fetchTemplates(this.canalSelecionadoId);
        this.templates = resp.templates || [];
        if (resp.aviso) this.erro = resp.aviso;
        // Auto-seleciona o template marcado como padrão; se não houver,
        // pega o primeiro APPROVED. Limpa seleção anterior se o template
        // não existir mais nesse canal.
        const aindaExiste = this.templates.find((t) => t.name === this.templateName);
        if (!aindaExiste) {
          const padrao = this.templates.find((t) => t.padrao_inicial)
            || this.templates.find((t) => t.status === 'APPROVED')
            || this.templates[0];
          this.templateName = padrao ? padrao.name : '';
        }
        this._prefillTemplate();
      } catch (error) {
        this.erro = `Falha ao carregar templates: ${error.message}`;
        this.templates = [];
      }
    },

    async atualizarTemplatesDaMeta() {
      if (!this.canalSelecionadoId) return;
      this.sincronizandoTemplates = true;
      this.erro = null;
      try {
        await this.sincronizarTemplates(this.canalSelecionadoId);
        await this._carregarTemplatesDoCanal();
        this.sucesso = 'Templates sincronizados com a Meta.';
        setTimeout(() => { this.sucesso = null; }, 2500);
      } catch (error) {
        const data = error.response && error.response.data;
        this.erro = (data && (data.error || data.details)) || error.message;
      } finally {
        this.sincronizandoTemplates = false;
      }
    },

    fechar() { this.$emit('close'); },

    async onBusca() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(async () => {
        if ((this.busca || '').trim().length < 2) { this.resultados = []; return; }
        this.buscando = true;
        try {
          this.resultados = await this.buscarContato(this.busca);
        } catch (_e) { this.resultados = []; }
        finally { this.buscando = false; }
      }, 300);
    },

    selecionarContato(c) {
      this.contatoSelecionado = c;
      this.modoSelecao = 'existente';
    },

    async avancar() {
      this.erro = null;
      this.sucesso = null;
      if (!this.podeAvancar) return;
      this.verificando = true;
      try {
        // Pra contato existente, verifica janela; pra novo número, sem histórico
        let info = { aberta: false, motivo: 'sem-mensagem-entrante', consentimento: 'pendente' };
        if (this.modoSelecao === 'existente' && this.contatoSelecionado) {
          info = await this.verificarJanela(this.contatoSelecionado.id);
        } else if (this.modoSelecao === 'novo') {
          const contatos = await this.buscarContato(this.novoTelefone);
          const contatoExistente = this._encontrarContatoExistentePorTelefone(contatos, this.novoTelefone);
          this.contatoSelecionado = contatoExistente || null;
          if (contatoExistente) {
            this.sucesso = 'Número já cadastrado. A conversa será vinculada ao contato existente.';
            info = await this.verificarJanela(contatoExistente.id);
          }
        }
        this.janela = info;
        // Carrega templates do canal selecionado (filtrados pela Meta).
        await this._carregarTemplatesDoCanal();
        // Decide modo de envio default baseado no bloqueio
        if (this.bloqueio && this.bloqueio.tipo === 'precisa-template') {
          this.modoEnvio = 'template';
        } else {
          this.modoEnvio = 'texto';
        }
        this.etapa = 2;
      } catch (error) {
        this.erro = `Falha ao verificar janela: ${error.message}`;
      } finally {
        this.verificando = false;
      }
    },

    voltar() {
      this.etapa = 1;
      this.erro = null;
      this.sucesso = null;
    },

    async enviar() {
      if (!this.podeEnviar) return;
      this.erro = null;
      this.sucesso = null;
      this.enviando = true;
      try {
        const payload = { modo: this.modoEnvio };
        // Sempre envia o canal escolhido — garante que template e envio
        // saiam pelo MESMO número (não pelo "primeiro ativo" do backend).
        if (this.canalSelecionadoId) payload.canal_whatsapp_id = this.canalSelecionadoId;
        if (this.modoSelecao === 'existente') {
          payload.contato_id = this.contatoSelecionado.id;
        } else {
          payload.telefone = this.novoTelefone.replace(/\D/g, '');
          if (this.novoNome) payload.nome = this.novoNome;
        }
        if (this.modoEnvio === 'texto') {
          payload.conteudo = this.conteudo;
        } else {
          payload.templateName = this.templateName;
          payload.templateValores = this.templateValores;
        }
        await this.iniciarConversa(payload);
        this.sucesso = 'Mensagem enviada! Conversa criada.';
        setTimeout(() => this.fechar(), 800);
      } catch (error) {
        const data = error.response && error.response.data;
        this.erro = (data && (data.error || data.details)) || error.message;
        // se backend sugeriu mudar pra template
        if (data && data.sugestao === 'modo=template') {
          this.modoEnvio = 'template';
        }
      } finally {
        this.enviando = false;
      }
    },

    /**
     * Auto-preenche os parâmetros do template com dados do contato:
     *   - nome → contato.nome (ou primeiro nome digitado em "novo número")
     * Outros parâmetros ficam vazios pra o atendente preencher.
     */
    _prefillTemplate() {
      const tpl = this.templateAtual;
      if (!tpl) return;
      const nomeContato = this.contatoSelecionado
        ? (this.contatoSelecionado.nome || '').split(/\s+/)[0]
        : (this.novoNome || '').split(/\s+/)[0];
      const novosValores = { ...this.templateValores };
      for (const p of tpl.parametros || []) {
        if (novosValores[p.nome]) continue; // não sobrescreve o que o atendente já mexeu
        if (p.nome === 'nome' && nomeContato) novosValores[p.nome] = nomeContato;
      }
      this.templateValores = novosValores;
    },
    _fmt(d) {
      if (!d) return '';
      try { return new Date(d).toLocaleString('pt-BR'); } catch (_e) { return ''; }
    },
    formatarTelefone(c) {
      const tel = (c.canais || []).find((x) => x.tipo_canal === 'telefone' && x.principal)
        || (c.canais || []).find((x) => x.tipo_canal === 'telefone');
      return tel ? (tel.normalizado || tel.valor_canal) : '—';
    },
    _normalizarTelefone(valor) {
      if (!valor) return null;
      let limpo = String(valor).replace(/[^\d+]/g, '');
      if (limpo.startsWith('+')) limpo = limpo.slice(1);
      if (limpo.length === 10 || limpo.length === 11) limpo = `55${limpo}`;
      if (limpo.length === 12 && !limpo.startsWith('55')) return limpo;
      return limpo;
    },
    _gerarVariantesTelefone(valor) {
      const normalizado = this._normalizarTelefone(valor);
      if (!normalizado) return [];

      const limpo = normalizado.replace(/\D/g, '');
      const telefoneLocal = limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)
        ? limpo.slice(2)
        : limpo;
      const variantes = new Set([normalizado]);

      if (telefoneLocal.length === 11 && telefoneLocal[2] === '9') {
        variantes.add(this._normalizarTelefone(`${telefoneLocal.slice(0, 2)}${telefoneLocal.slice(3)}`));
      }

      if (telefoneLocal.length === 10) {
        variantes.add(this._normalizarTelefone(`${telefoneLocal.slice(0, 2)}9${telefoneLocal.slice(2)}`));
      }

      return [...variantes].filter(Boolean);
    },
    _encontrarContatoExistentePorTelefone(contatos, telefone) {
      const variantes = new Set(this._gerarVariantesTelefone(telefone));
      if (!variantes.size) return null;
      return (contatos || []).find((contato) => (
        (contato.canais || []).some((canal) => (
          canal.tipo_canal === 'telefone'
          && variantes.has(this._normalizarTelefone(canal.normalizado || canal.valor_canal))
        ))
      )) || null;
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div class="modal-backdrop-custom" @click.self="fechar">
      <div class="modal-card shadow-lg">
        <header class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <h6 class="m-0">
            <i class="ri-chat-new-line me-1"></i>
            {{ etapa === 1 ? 'Iniciar nova conversa' : `Mensagem para ${contatoAtivoNome}` }}
          </h6>
          <button type="button" class="btn btn-link p-0 text-body" @click="fechar">
            <i class="ri-close-line" style="font-size: 1.4rem;"></i>
          </button>
        </header>

        <div class="modal-body-custom p-3">
          <!-- ETAPA 1 — selecionar contato -->
          <div v-if="etapa === 1">
            <div class="btn-group btn-group-sm w-100 mb-3" role="group">
              <button
                type="button"
                class="btn"
                :class="modoSelecao === 'existente' ? 'btn-primary' : 'btn-outline-primary'"
                @click="modoSelecao = 'existente'"
              >
                <i class="ri-user-search-line me-1"></i>Contato existente
              </button>
              <button
                type="button"
                class="btn"
                :class="modoSelecao === 'novo' ? 'btn-primary' : 'btn-outline-primary'"
                @click="modoSelecao = 'novo'"
              >
                <i class="ri-add-line me-1"></i>Novo número
              </button>
            </div>

            <!-- Buscar existente -->
            <div v-if="modoSelecao === 'existente'">
              <input
                v-model="busca"
                type="text"
                class="form-control mb-2"
                placeholder="Buscar por nome ou telefone (min 2 caracteres)..."
                @input="onBusca"
              />
              <div v-if="buscando" class="text-muted small text-center py-2">
                <i class="ri-loader-4-line spin me-1"></i>buscando...
              </div>
              <div v-else-if="busca.length >= 2 && !resultados.length" class="text-muted small text-center py-3">
                Nenhum contato encontrado. Tente outro termo ou cadastre como novo.
              </div>
              <ul v-else class="list-group resultados-busca">
                <li
                  v-for="c in resultados"
                  :key="c.id"
                  class="list-group-item list-group-item-action"
                  :class="{ active: contatoSelecionado && contatoSelecionado.id === c.id }"
                  @click="selecionarContato(c)"
                >
                  <div class="fw-semibold">{{ c.nome || `Contato #${c.id}` }}</div>
                  <small class="text-muted">{{ formatarTelefone(c) }} {{ c.cidade ? '· ' + c.cidade : '' }}</small>
                </li>
              </ul>
            </div>

            <!-- Novo número -->
            <div v-else>
              <div class="mb-2">
                <label class="form-label small">Telefone (somente Brasil)</label>
                <div class="input-group">
                  <span class="input-group-text">+55</span>
                  <input
                    v-model="novoTelefone"
                    type="text"
                    class="form-control"
                    placeholder="31999999999"
                    maxlength="15"
                  />
                </div>
                <small class="text-muted">Inclua DDD. Min 10 dígitos.</small>
              </div>
              <div class="mb-2">
                <label class="form-label small">Nome (opcional)</label>
                <input v-model="novoNome" type="text" class="form-control" placeholder="Como deseja identificar este contato?" />
              </div>
            </div>
          </div>

          <!-- ETAPA 2 — janela + envio -->
          <div v-else>
            <!-- Seletor de canal: garante que o atendente sabe por qual
                 número está enviando e que os templates listados são
                 dele (não vazam de outros canais). -->
            <div class="mb-2">
              <label class="form-label small mb-1">Canal WhatsApp</label>
              <div v-if="carregandoCanais" class="text-muted small">
                <i class="ri-loader-4-line spin me-1"></i>carregando canais...
              </div>
              <div v-else-if="!canais.length" class="alert alert-warning py-2 mb-0 small">
                <i class="ri-error-warning-line me-1"></i>
                Nenhum canal WhatsApp ativo. Conecte um em Cadastros → Canais WhatsApp.
              </div>
              <select v-else v-model="canalSelecionadoId" class="form-select form-select-sm">
                <option v-for="c in canais" :key="c.id" :value="c.id">
                  {{ c.display_name || c.telefone_formatado || ('Canal #' + c.id) }}
                  <span v-if="c.telefone_formatado && c.display_name"> ({{ c.telefone_formatado }})</span>
                </option>
              </select>
            </div>

            <div v-if="bloqueio && bloqueio.tipo === 'opt_out'" class="alert alert-danger">
              <i class="ri-forbid-line me-1"></i>{{ bloqueio.mensagem }}
            </div>

            <template v-else>
              <div v-if="janela && janela.aberta" class="alert alert-success py-2 mb-2">
                <i class="ri-checkbox-circle-line me-1"></i>
                <strong>Janela aberta</strong> até {{ _fmt(janela.expira_em) }}.
                Texto livre permitido.
              </div>
              <div v-else-if="bloqueio && bloqueio.tipo === 'precisa-template'" class="alert alert-warning py-2 mb-2">
                <i class="ri-lock-2-line me-1"></i>{{ bloqueio.mensagem }}
              </div>

              <!-- Toggle modo de envio (só faz sentido se janela aberta) -->
              <div v-if="janela && janela.aberta && janela.consentimento === 'opt_in'" class="btn-group btn-group-sm mb-2" role="group">
                <button type="button" class="btn" :class="modoEnvio === 'texto' ? 'btn-primary' : 'btn-outline-primary'" @click="modoEnvio = 'texto'">
                  <i class="ri-message-2-line me-1"></i>Texto livre
                </button>
                <button type="button" class="btn" :class="modoEnvio === 'template' ? 'btn-primary' : 'btn-outline-primary'" @click="modoEnvio = 'template'">
                  <i class="ri-file-list-2-line me-1"></i>Template
                </button>
              </div>

              <!-- Modo texto -->
              <div v-if="modoEnvio === 'texto'">
                <textarea
                  v-model="conteudo"
                  class="form-control"
                  rows="4"
                  placeholder="Digite sua mensagem..."
                  :disabled="bloqueio !== null"
                ></textarea>
              </div>

              <!-- Modo template -->
              <div v-else>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <label class="form-label small mb-0">Template HSM (da Meta)</label>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 text-decoration-none"
                    :disabled="sincronizandoTemplates || !canalSelecionadoId"
                    @click="atualizarTemplatesDaMeta"
                  >
                    <i :class="sincronizandoTemplates ? 'ri-loader-4-line spin' : 'ri-refresh-line'" class="me-1"></i>
                    {{ sincronizandoTemplates ? 'Sincronizando...' : 'Atualizar da Meta' }}
                  </button>
                </div>
                <div v-if="!templates.length" class="alert alert-info py-2 small mb-2">
                  <i class="ri-information-line me-1"></i>
                  Nenhum template aprovado disponível neste canal.
                  Clique em "Atualizar da Meta" pra sincronizar.
                </div>
                <select v-else v-model="templateName" class="form-select form-select-sm mb-2">
                  <option v-for="t in templates" :key="t.id || t.name" :value="t.name">
                    {{ t.name }}{{ t.padrao_inicial ? ' (padrão)' : '' }}{{ t.categoria ? ' · ' + t.categoria : '' }} · {{ t.language }}
                  </option>
                </select>
                <div v-if="templateAtual">
                  <div class="mb-2 p-2 border rounded template-preview-box">
                    <small class="text-muted d-block mb-1">Prévia:</small>
                    <div class="small">{{ templateAtual.preview }}</div>
                  </div>
                  <div v-for="p in templateAtual.parametros" :key="p.indice" class="mb-2">
                    <label class="form-label small">{{ p.label }}{{ p.obrigatorio ? ' *' : '' }}</label>
                    <input
                      v-model="templateValores[p.nome]"
                      type="text"
                      class="form-control form-control-sm"
                      :placeholder="p.label"
                    />
                  </div>
                </div>
              </div>
            </template>

            <div v-if="erro" class="alert alert-danger mt-2 mb-0 py-2 small">
              <i class="ri-error-warning-line me-1"></i>{{ erro }}
            </div>
            <div v-if="sucesso" class="alert alert-success mt-2 mb-0 py-2 small">
              <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
            </div>
          </div>
        </div>

        <footer class="d-flex justify-content-between gap-2 px-3 py-2 border-top">
          <div>
            <button v-if="etapa === 2" type="button" class="btn btn-outline-secondary btn-sm" @click="voltar">
              <i class="ri-arrow-left-line me-1"></i>Voltar
            </button>
          </div>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="fechar">Cancelar</button>
            <button
              v-if="etapa === 1"
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!podeAvancar || verificando"
              @click="avancar"
            >
              <i v-if="verificando" class="ri-loader-4-line spin me-1"></i>
              <i v-else class="ri-arrow-right-line me-1"></i>{{ verificando ? 'Verificando...' : 'Próximo' }}
            </button>
            <button
              v-else
              type="button"
              class="btn btn-success btn-sm"
              :disabled="!podeEnviar || (bloqueio && bloqueio.tipo === 'opt_out')"
              @click="enviar"
            >
              <i v-if="enviando" class="ri-loader-4-line spin me-1"></i>
              <i v-else class="ri-send-plane-2-line me-1"></i>{{ enviando ? 'Enviando...' : 'Enviar' }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border-radius: 0.5rem;
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.modal-body-custom {
  overflow-y: auto;
  flex: 1;
}
.resultados-busca {
  max-height: 250px;
  overflow-y: auto;
}
.list-group-item {
  cursor: pointer;
}
/* Prévia do template — usa tokens do tema pra funcionar em light e dark mode. */
.template-preview-box {
  background: var(--vz-tertiary-bg, var(--vz-light, #f3f6f9));
  color: var(--vz-body-color);
  border-color: var(--vz-border-color) !important;
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
